import { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";
import Seo from "../components/Seo";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Crown, Pill, Droplet, Music, Palette,
  Footprints, ExternalLink, Heart, CheckCircle2, DollarSign, Shirt,
  Gift, Mail, Lock, Star, Zap, ShieldCheck, Award, Download,
} from "lucide-react";

/* /m/:handle — a member's personal Faitharama mirror site.
   REDESIGNED PER MIKE'S BRIEF:
   - Lead-capture FIRST (name + email → Day-1 PDF + free membership + free training)
   - Bundle messaging ("get these 3 things free")
   - Small upsell (included Coaching Cert) so newbies feel they're getting ahead
   - Slow horizontal product scroller advertising everything Faitharama sells
   - Multi-color palette (coral, teal, navy, amber, wine) — NOT all green
   - Short-haired grandma photo stays in the hero (Mike's favorite)            */

const WINE  = "#0F172A";
const O     = "#F39A1F";
const G     = "#2E9F3A";
const GREEN = "#2E9F3A";
const TEAL  = "#287F8E";
const CORAL = "#5BA9D6";  // Baby blue per Mike's directive (was pink/red #E63946)
const NAVY = "#0F172A";
const GOLD  = "#E0F2FE";

function addRef(url, code) {
  if (!url || !code) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}ref=${encodeURIComponent(code)}`;
}

/* All-products slow-scroller items — visitors see what they can SHARE. */
const SCROLLER = [
  { img: "/courses/aff101-tile-editorial-cover.png", caption: "Affiliate 101",           tone: NAVY },
  { img: "/with-faith/cover.png",                    caption: "With Faith All Things Are Possible", tone: NAVY },
  { img: "/courses/pro-affiliate-101.png",           caption: "Beginner Playbook",        tone: NAVY },
  { img: "/products/piurify-echo-flask.png",         caption: "Piurify Hydrogen",         tone: TEAL },
  { img: "/products/medicinal-seed-kit.png",         caption: "Medicinal Seed Kit",       tone: G },
  { img: "/marquee/faitharama-merch.png",            caption: "Faitharama Merch",         tone: O },
];


export default function MirrorSite() {
  const { handle }     = useParams();
  const { user }       = useAuth();
  const [m, setM]      = useState(null);
  // A/B: pick a stable variant per visitor per handle (localStorage so refresh keeps the same)
  const [variant, setVariant] = useState("a");
  useEffect(() => {
    if (!handle) return;
    const key = `mirror_variant::${handle}`;
    let v = localStorage.getItem(key);
    if (v !== "a" && v !== "b") {
      v = Math.random() < 0.5 ? "a" : "b";
      localStorage.setItem(key, v);
    }
    setVariant(v);
  }, [handle]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Lead-capture state
  const [form, setForm]       = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone]       = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    api.get(`/mirror/${handle}`)
      .then((r) => setM(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [handle]);

  // Log a view event once per session for A/B stats.
  useEffect(() => {
    if (!m || !handle) return;
    const v = m.ab_enabled ? variant : "a";
    const key = `mirror_viewed::${handle}::${v}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/mirror/${handle}/track`, { event: "view", variant: v }).catch(() => {});
  }, [m, handle, variant]);

  // Variant-aware copy: when ab_enabled and the visitor was assigned "b",
  // override headline/tagline/photo/upsell_title with the B fields IF they
  // were filled out. Empty B fields fall back to the A copy.
  const useB = m?.ab_enabled && variant === "b";
  const view = m ? {
    ...m,
    headline:      useB && m.variant_b_headline    ? m.variant_b_headline    : m.headline,
    tagline:       useB && m.variant_b_tagline     ? m.variant_b_tagline     : m.tagline,
    photo_url:     useB && m.variant_b_photo_url   ? m.variant_b_photo_url   : m.photo_url,
    upsell_title:  useB && m.variant_b_upsell_title? m.variant_b_upsell_title: m.upsell_title,
  } : null;

  // Helper for any CTA click (main upsell, free gift) — pings the tracker so
  // the member can see which variant their visitors actually click on.
  const trackEvent = (event) => {
    if (!handle) return;
    const v = m?.ab_enabled ? variant : "a";
    api.post(`/mirror/${handle}/track`, { event, variant: v }).catch(() => {});
  };

  // Drop the ref code into localStorage so even if the visitor clicks
  // around and comes back through Google a week later, the cookie sticks.
  useEffect(() => {
    if (m?.affiliate_code) {
      try { localStorage.setItem("faitharama_ref", m.affiliate_code); }
      catch (_e) { /* no-op */ }
    }
  }, [m?.affiliate_code]);

  if (notFound) return <Navigate to="/" replace />;
  if (loading || !m) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFFFFF" }}>
        <p className="italic text-sm" style={{ color: WINE }}>Loading…</p>
      </div>
    );
  }

  const code      = m.affiliate_code;
  const firstName = (m.full_name || "").split(" ")[0] || m.handle;

  const submitBundle = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Just your first name and email — both fields please.");
    }
    setSending(true);
    try {
      await api.post("/leads/30-day-challenge", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        ref_code: code,
      });
      setDone(true);
      toast.success("★ Bundle on its way! Check your email.");
      // Auto-trigger the PDF download
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = "/free-gifts/30-day-challenge-day-1-sample.pdf";
        a.download = "Day-1-Sample-With-Faith-30-Day-Challenge.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, 700);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }} data-testid="mirror-page">
      {/* Per-mirror SEO — Mike's name + photo + custom headline become the
          Google title, description, and Facebook share preview. */}
      <Seo
        title={`${m.full_name} on Faitharama — Free Book + 50% Affiliate Path`}
        description={`${m.headline || `Welcome — I'm ${m.full_name.split(' ')[0]}.`} ${m.tagline || "Faith, health, and ministry the easy way. Grab the free Day-1 PDF, free membership, and free affiliate training."}`}
        canonical={`https://faitharama.com/m/${m.handle || handle}`}
        image={m.photo_url || "https://faitharama.com/og-default.png"}
        type="profile"
        keywords={`${m.full_name}, Faitharama, Christian wellness, faith mentor, free Christian bundle, ${m.handle || handle}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "mainEntity": {
            "@type": "Person",
            "name": m.full_name,
            "url": `https://faitharama.com/m/${m.handle || handle}`,
            "description": m.tagline || "Faitharama member helping Christians grow in faith and wellness.",
            "image": m.photo_url || "https://faitharama.com/og-default.png",
            "memberOf": { "@type": "Organization", "name": "Faitharama", "url": "https://faitharama.com" }
          }
        }}
      />

      {/* ─────── Slim header (faitharama bar only — no full nav) ─────── */}
      <header className="border-b-2"
              style={{ background: "#EAF6FC", borderColor: GREEN }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="font-serif-display text-xl md:text-2xl uppercase tracking-tight" style={{ color: WINE }}>
            Faitharama<span style={{ color: GREEN }}>.com</span>
          </Link>
          <a href="#bundle"
             data-testid="mirror-header-cta"
             className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
             style={{ background: CORAL }}>
            Grab Your Free Book <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </header>

      {/* ─────── CAPTURE-PAGE HERO (designed by the member) ─────── */}
      <CapturePageHero m={m} view={view} handle={handle} trackEvent={trackEvent} />

      {/* ─────── (Legacy hero removed — Capture-Page Hero above now handles it) ─────── */}

      {/* ═══════════════ #1 — LEAD CAPTURE: ONE GIVEAWAY ═══════════════
          The 12-page Faitharama Free Guide + 7-day free trial.
          NO "3 gifts" pitch — Mike's directive (Jun 2026). */}
      <section id="bundle"
               className="border-y-4"
               style={{ background: `linear-gradient(180deg, #D9EEFB 0%, #EAF6FC 100%)`, borderColor: GREEN }}
               data-testid="mirror-bundle">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">

            {/* LEFT — the free guide cover */}
            <div className="flex justify-center order-2 md:order-1">
              <div className="relative w-full max-w-[420px]">
                <div className="absolute -inset-6 rounded-2xl blur-3xl opacity-30"
                     style={{ background: `radial-gradient(circle, ${O} 0%, transparent 70%)` }}/>
                <img src="/free-guide/free-guide-cover.png"
                     alt="Faitharama Free Guide — 12 pages, picture-first"
                     loading="lazy"
                     className="relative w-full h-auto rounded-xl shadow-2xl"
                     style={{ border: `4px solid ${O}` }}/>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold shadow-lg text-white whitespace-nowrap"
                     style={{ background: GREEN }}>
                  ★ YOURS · FREE
                </div>
              </div>
            </div>

            {/* RIGHT — pitch + form */}
            <div className="order-1 md:order-2 text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold mb-4 text-white"
                   style={{ background: CORAL }}>
                <Gift className="w-3.5 h-3.5"/> ★ 100% FREE · NO CARD
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[1.05]"
                  style={{ color: WINE, textWrap: "balance" }}>
                Here's The <span style={{ color: O }}>Free Guide</span> That'll Reveal What We're Doin' At Faitharama.com.
              </h2>

              {/* Bullet list of what's inside */}
              <ul className="mt-5 space-y-2">
                {[
                  "An all-in-one platform built for Christian seniors",
                  "Funnel Builder — share Faitharama with one easy link",
                  "AI Writer & 24/7 Christian AI Mentor",
                  "Book Cover Creator + Appointment Scheduler",
                  "Affiliate Income + Earnings Dashboard",
                  "A community that prays for you",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-base md:text-lg" style={{ color: "#3A2A1F" }}>
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: GREEN }}/>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              {/* THE FORM (or success state) */}
              {!done ? (
                <form ref={formRef} onSubmit={submitBundle}
                      data-testid="mirror-bundle-form"
                      className="mt-6 space-y-2.5 max-w-md">
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                         data-testid="mirror-bundle-name"
                         placeholder="Your first name"
                         className="w-full px-4 py-4 rounded-lg text-base focus:ring-4 outline-none text-foreground"
                         style={{ background: "#FFFFFF", border: `2px solid ${O}` }}/>
                  <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                         data-testid="mirror-bundle-email"
                         type="email"
                         placeholder="Your email"
                         className="w-full px-4 py-4 rounded-lg text-base focus:ring-4 outline-none text-foreground"
                         style={{ background: "#FFFFFF", border: `2px solid ${O}` }}/>
                  <button type="submit" disabled={sending}
                          data-testid="mirror-bundle-submit"
                          className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg text-base font-bold uppercase tracking-[0.16em] shadow-lg hover:scale-[1.02] transition disabled:opacity-50 text-white"
                          style={{ background: CORAL }}>
                    {sending ? "Sending…" : <>Send Me The Free Guide <ArrowRight className="w-4 h-4"/></>}
                  </button>
                  <p className="text-[11px] italic" style={{ color: "#5A4030" }}>
                    We respect your inbox. Unsubscribe any time. No card needed.
                  </p>
                </form>
              ) : (
                <div className="mt-6 rounded-2xl p-5 max-w-md shadow-lg"
                     style={{ background: "#FFFFFF", border: `2px solid ${G}` }}
                     data-testid="mirror-bundle-success">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center"
                         style={{ background: G, color: "#FFFFFF" }}>
                      <CheckCircle2 className="w-5 h-5"/>
                    </div>
                    <div className="font-serif-display text-2xl" style={{ color: WINE }}>
                      Sent! Check your inbox.
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "#3A2A1F" }}>
                    Your free Faitharama Guide is on the way. Your download should also be starting now.
                  </p>
                  <a href="/faitharama-free-guide.pdf"
                     download="Faitharama-Free-Guide.pdf"
                     className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow"
                     style={{ background: G }}>
                    <Download className="w-4 h-4"/> Download Now
                  </a>
                </div>
              )}

              {/* 7-day trial CTA — secondary */}
              <div className="mt-6 rounded-xl p-4 flex items-start gap-3"
                   style={{ background: "#FFFFFF", border: `2px solid ${GREEN}` }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white"
                     style={{ background: GREEN }}>
                  <Star className="w-4 h-4"/>
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: GREEN }}>
                    ★ OR JUMP RIGHT IN
                  </div>
                  <div className="text-base font-bold mt-0.5" style={{ color: WINE }}>
                    Start Your 7-Day Free Trial Today.
                  </div>
                  <p className="text-sm mt-1 leading-snug" style={{ color: "#3A2A1F" }}>
                    Full access to every tool for 7 days. No card needed. Cancel any time before day 7.
                  </p>
                  <a href="/signup" data-testid="mirror-trial-cta"
                     className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.16em] hover:underline"
                     style={{ color: GREEN }}>
                    Start My 7-Day Trial <ArrowRight className="w-4 h-4"/>
                  </a>
                </div>
              </div>

              {/* Trust mini-row */}
              <div className="mt-5 flex flex-wrap items-center gap-3 text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: "#5A4030" }}>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: G }}/> No Spam</span>
                <span style={{ color: O }}>·</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" style={{ color: G }}/> No Card</span>
                <span style={{ color: O }}>·</span>
                <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" style={{ color: CORAL }}/> Faitharama.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ #2 — SLOW PRODUCT SCROLLER ═══════════════
          "Here's everything Faitharama sells" — a slow horizontal marquee.
          Auto-pauses on hover. Uses CSS keyframes so it runs even on phones. */}
      <section id="products" className="py-12 md:py-14"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)" }}
               data-testid="mirror-scroller-wrap">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center mb-7">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GREEN }}>
            ★ EVERYTHING YOU CAN SHARE
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-1"
              style={{ color: WINE, textWrap: "balance" }}>
            From Hydrogen Bottles<br/>To Bible Studies.
          </h2>
        </div>

        <style>{`
          @keyframes mirrorSlowScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .mirror-scroller { display: flex; gap: 18px; animation: mirrorSlowScroll 60s linear infinite; will-change: transform; }
          .mirror-scroller:hover { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) { .mirror-scroller { animation: none; } }
        `}</style>

        <div className="overflow-hidden py-2" data-testid="mirror-scroller">
          <div className="mirror-scroller">
            {[...SCROLLER, ...SCROLLER].map((s, i) => (
              <div key={i}
                   className="rounded-2xl overflow-hidden shadow-md bg-white flex-shrink-0"
                   style={{ width: 260, border: `3px solid ${s.tone}` }}>
                <div className="aspect-[4/3] overflow-hidden" style={{ background: "#EAF6FC" }}>
                  <img src={s.img} alt={s.caption} loading="lazy"
                       className="block w-full h-full object-contain p-3"/>
                </div>
                <div className="px-3 py-2.5 text-center font-serif-display leading-tight"
                     style={{ color: s.tone, background: "#FFFFFF", fontSize: 16 }}>
                  {s.caption}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-5 text-center text-sm italic max-w-xl mx-auto" style={{ color: "#3A2A1F" }}>
          Pause anytime by hovering. Every one of these earns you commission when you share through {firstName}&apos;s link.
        </p>
      </section>

      {/* ═══════════════ #3 — SMALL UPSELL ═══════════════
          Faitharama Fast Start Guide — the entry guide that
          gets newbies moving without scaring them off. Replaces the
          old "included Coaching Cert" box per Mike's call (May 2026):
          "tear it down, looks horrible and priced wrong". */}
      <section className="border-y-2"
               style={{ background: `linear-gradient(135deg, #EAF6FC 0%, #D9EEFB 100%)`, borderColor: GREEN }}
               data-testid="mirror-upsell">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-14 grid md:grid-cols-[1.1fr_1fr] gap-8 md:gap-12 items-center">
          {/* LEFT — the real bundle picture Mike sent */}
          <div className="flex justify-center">
            <img src="/free-guide/fast-start-bundle.png"
                 alt="Faitharama Fast Start Guide — Make $1,000 in 90 days · included"
                 loading="lazy"
                 className="w-full h-auto max-w-[560px] rounded-xl shadow-2xl"
                 style={{ filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.18))" }}/>
          </div>
          {/* RIGHT — pitch + CTA */}
          <div className="text-center md:text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: GREEN }}>
              ★ ONE-TIME · NOT A SUBSCRIPTION · included
            </div>
            <h3 className="font-serif-display text-3xl md:text-5xl uppercase leading-tight mt-1"
                style={{ color: WINE, textWrap: "balance" }}>
              Make <span style={{ color: GREEN }}>$1,000</span> In 90 Days.
            </h3>
            <p className="mt-3 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "#3A2A1F" }}>
              The Faitharama plain-English playbook that shows you exactly how — even during your 7-day free trial. <strong>24-page PDF guide + 7 short video walk-throughs</strong>.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: "#5A4030" }}>
              <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5" style={{ color: O }}/> Instant Download</span>
              <span style={{ color: O }}>·</span>
              <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" style={{ color: O }}/> Lifetime Access</span>
            </div>
            <div className="mt-5">
              <Link to={addRef("/starter-guide", code)}
                    data-testid="mirror-upsell-buy"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] shadow-xl hover:scale-[1.03] transition text-white"
                    style={{ background: GREEN }}>
                Get It · included <ArrowRight className="w-5 h-5"/>
              </Link>
              <div className="mt-2 text-[11px] italic" style={{ color: "#5A4030" }}>One-time payment · No subscription</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ #4 — THE 3 TIERS ═══════════════
          Free / Builder / Pro pricing right on the mirror site so visitors
          can decide on the spot.  Each Join button auto-credits the
          affiliate via addRef. */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="mirror-tiers">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ PICK YOUR PLAN · JOIN THROUGH {firstName.toUpperCase()}
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-1"
              style={{ color: WINE, textWrap: "balance" }}>
            Free · <span style={{ color: O }}>$49</span> · <span style={{ color: G }}>$79</span>
          </h2>
          <p className="mt-3 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            Three ways to join. <strong>Same products either way</strong> — paid tiers just
            unlock the tools to build your own ministry &amp; resell.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto items-stretch">
          {/* FREE — MEMBER */}
          <div className="rounded-2xl p-6 md:p-7 bg-white shadow-md flex flex-col"
               style={{ border: `3px solid ${TEAL}55` }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: `${TEAL}1F`, color: TEAL }}>
                <Heart className="w-5 h-5"/>
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: TEAL }}>
                MEMBER
              </div>
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-serif-display text-5xl md:text-6xl leading-none" style={{ color: TEAL }}>$0</span>
              <span className="text-base font-bold" style={{ color: TEAL }}>/forever</span>
            </div>
            <ul className="mt-4 space-y-2 text-base flex-1" style={{ color: "#0F172A" }}>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: TEAL }}/> 30-Day Faith &amp; Wellness Challenge</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: TEAL }}/> Free Jukebox + community</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: TEAL }}/> Earn <strong>50%</strong> on every sale forever</li>
            </ul>
            <Link to={addRef("/signup?plan=free", code)}
                  data-testid="mirror-tier-free"
                  className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow hover:scale-[1.02] transition"
                  style={{ background: TEAL }}>
              Start Free Trial <ArrowRight className="w-4 h-4"/>
            </Link>
            <div className="text-[11px] italic text-foreground/60 text-center mt-2">No credit card</div>
          </div>

          {/* BUILDER — $49  (highlighted as most popular) */}
          <div className="rounded-2xl p-6 md:p-7 bg-white shadow-2xl flex flex-col relative"
               style={{ border: `3px solid ${O}`, transform: "scale(1.02)" }}>
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold whitespace-nowrap shadow text-white"
                  style={{ background: O }}>
              ★ MOST POPULAR
            </span>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: O }}>
                <Zap className="w-5 h-5"/>
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>
                MINISTRY BUILDER
              </div>
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-serif-display text-5xl md:text-6xl leading-none" style={{ color: O }}>$49</span>
              <span className="text-base font-bold" style={{ color: O }}>/month</span>
            </div>
            <ul className="mt-4 space-y-2 text-base flex-1" style={{ color: "#0F172A" }}>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: O }}/> Everything in Member, plus:</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: O }}/> Your own Mirror Site (this page!)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: O }}/> AI Writer AI (10k words/mo)</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: O }}/> <em>Plastic Detox</em> book free</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: O }}/> Earn <strong>50%</strong> on every sale forever</li>
            </ul>
            <Link to={addRef("/signup?plan=faithful_monthly", code)}
                  data-testid="mirror-tier-builder"
                  className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.16em] shadow-xl hover:scale-[1.03] transition text-white"
                  style={{ background: O }}>
              Start Building · $49 <ArrowRight className="w-4 h-4"/>
            </Link>
            <div className="text-[11px] italic text-center mt-2" style={{ color: "#5A4030" }}>Cancel anytime</div>
          </div>

          {/* PRO — $79 */}
          <div className="rounded-2xl p-6 md:p-7 bg-white shadow-md flex flex-col"
               style={{ border: `3px solid ${WINE}` }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${WINE}1F`, color: WINE }}>
                <Crown className="w-5 h-5"/>
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: WINE }}>
                MINISTRY PRO
              </div>
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-serif-display text-5xl md:text-6xl leading-none" style={{ color: WINE }}>$79</span>
              <span className="text-base font-bold" style={{ color: WINE }}>/month</span>
            </div>
            <ul className="mt-4 space-y-2 text-base flex-1" style={{ color: "#0F172A" }}>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: WINE }}/> Everything in Builder, plus:</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: WINE }}/> Unlimited AI Writer + contacts</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: WINE }}/> All Courses — Faitharama Academy</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: WINE }}/> Tripwire + email automation engine</li>
              <li className="flex gap-2"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: WINE }}/> Earn <strong>50%</strong> + product resale margins</li>
            </ul>
            <Link to={addRef("/signup?plan=kingdom_monthly", code)}
                  data-testid="mirror-tier-pro"
                  className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow hover:scale-[1.02] transition"
                  style={{ background: GREEN }}>
              Go Pro · $79 <ArrowRight className="w-4 h-4"/>
            </Link>
            <div className="text-[11px] italic text-foreground/60 text-center mt-2">Cancel anytime</div>
          </div>
        </div>

        <p className="mt-6 text-center text-sm md:text-base italic max-w-3xl mx-auto" style={{ color: "#0F172A" }}>
          ★ All three plans credit <strong>{firstName}</strong> automatically when you join through this page.
        </p>
      </section>

      {/* ═══════════════ FINAL BIG JOIN CTA — KEEP MIKE'S GREEN BOX VIBE ═══════════════ */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-16">
        <div className="rounded-3xl p-8 md:p-12 text-center shadow-xl"
             style={{ background: `linear-gradient(135deg, ${G} 0%, #1F8A2A 100%)`, border: `4px solid ${O}` }}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-4"
               style={{ background: O, color: "#0F172A" }}>
            <Crown className="w-3.5 h-3.5" /> ★ JOIN THROUGH {firstName.toUpperCase()} ★
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[1.05] mx-auto max-w-3xl"
              style={{ color: "#FFFFFF", textWrap: "balance" }}>
            It&apos;s Time For Christians To Stand Up For Their <span style={{ color: GOLD }}>Faith And Wellness!</span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg" style={{ color: "rgba(255,253,247,0.92)" }}>
            7-Day Free Trial · No credit card · Hand-picked Christian content + wellness for the whole family.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href="#bundle"
               data-testid="mirror-cta-bundle"
               className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
               style={{ background: CORAL }}>
              <Gift className="w-4 h-4" /> Grab The Free Book
            </a>
            <Link to={addRef("/pricing", code)}
                  data-testid="mirror-cta-pricing"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em]"
                  style={{ background: "#FFFFFF", color: WINE }}>
              See All 3 Plans <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-4 text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "rgba(255,253,247,0.75)" }}>
            ★ {firstName} earns commission on every Faitharama sale you make ★
          </p>
        </div>
      </section>

      {/* ─────────── MEMBER'S OWN FUNNEL — free gift + paid upsell ─────────── */}
      <MemberFunnel data={view || m} firstName={firstName} code={code} onCtaClick={() => trackEvent("cta")} onBumpClick={() => trackEvent("bump")} />

      <SiteFooter />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MEMBER'S PERSONAL FUNNEL — only renders if the member set up a free
   gift OR an upsell in their Mirror Site editor. Free section first
   (lead capture), then paid upsell.
   ════════════════════════════════════════════════════════════════════ */
function MemberFunnel({ data, firstName, code, onCtaClick, onBumpClick }) {
  if (!data) return null;
  const hasGift   = !!(data.free_gift_title || data.free_gift_description);
  const hasUpsell = !!(data.upsell_title || data.upsell_description || data.upsell_cta_url);
  const hasProduct = !!data.product_image_url;
  if (!hasGift && !hasUpsell && !hasProduct) return null;

  return (
    <section className="py-12 md:py-16 border-t-4" data-testid="mirror-member-funnel"
             style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)", borderColor: GREEN }}>
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: O }}>
            ★ {firstName.toUpperCase()}'S PERSONAL OFFER
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]" style={{ color: WINE }}>
            What I Hand-Picked For You.
          </h2>
        </div>

        {/* PRODUCT IMAGE + UPSELL — side by side on desktop */}
        {(hasProduct || hasUpsell) && (
          <div className="grid md:grid-cols-2 gap-6 items-center bg-white rounded-2xl shadow-md p-5 md:p-7 border-2 mb-8"
               style={{ borderColor: WINE }}>
            {hasProduct ? (
              <div className="aspect-square w-full rounded-xl overflow-hidden flex items-center justify-center"
                   style={{ background: "#FAFAFA" }}>
                <img src={data.product_image_url} alt={data.upsell_title || "Product"}
                     className="w-full h-full object-contain" loading="lazy"/>
              </div>
            ) : (
              <div className="aspect-square w-full rounded-xl flex items-center justify-center"
                   style={{ background: "#FAFAFA", border: `2px dashed ${O}55` }}>
                <span className="font-bold uppercase tracking-[0.22em] text-sm" style={{ color: `${O}AA` }}>
                  Product Image
                </span>
              </div>
            )}
            <div>
              {hasUpsell ? (
                <>
                  {data.upsell_title && (
                    <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mb-2"
                        style={{ color: WINE }}>
                      {data.upsell_title}
                    </h3>
                  )}
                  {data.upsell_description && (
                    <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: NAVY }}>
                      {data.upsell_description}
                    </p>
                  )}
                  {data.upsell_price > 0 && (
                    <div className="text-3xl font-bold mb-4" style={{ color: WINE }}>
                      ${Number(data.upsell_price).toFixed(2)}
                    </div>
                  )}
                  {data.upsell_cta_url && (
                    <a href={addRef(data.upsell_cta_url, code)}
                       target="_blank" rel="noopener noreferrer"
                       onClick={onCtaClick}
                       data-testid="mirror-member-upsell-cta"
                       className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                       style={{ background: WINE }}>
                      <ShieldCheck className="w-4 h-4"/> {data.upsell_cta_label || "Get It"}
                    </a>
                  )}

                  {/* ─── ORDER BUMP — small cross-sell card next to the main CTA. ─── */}
                  {data.bump_enabled && (data.bump_headline || data.bump_image_url) && (
                    <div className="mt-5 rounded-xl border-4 border-dashed p-3 md:p-4 max-w-md mx-auto"
                         data-testid="mirror-member-bump"
                         style={{ background: "#FFFFFF", borderColor: "#E8B539" }}>
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>
                        ★ One More Thing
                      </div>
                      <div className="flex items-center gap-3">
                        {data.bump_image_url && (
                          <img src={data.bump_image_url} alt={data.bump_headline || ""}
                               className="w-16 h-16 object-cover rounded border bg-white flex-shrink-0" />
                        )}
                        <div className="flex-1 text-left">
                          {data.bump_headline && (
                            <div className="font-bold text-sm leading-tight" style={{ color: WINE }}>
                              {data.bump_headline}
                            </div>
                          )}
                          {data.bump_pitch && (
                            <div className="text-xs mt-0.5" style={{ color: "#3A2F23" }}>
                              {data.bump_pitch}
                            </div>
                          )}
                        </div>
                      </div>
                      {data.bump_cta_url && (
                        <a href={addRef(data.bump_cta_url, code)}
                           target="_blank" rel="noopener noreferrer"
                           onClick={onBumpClick}
                           data-testid="mirror-bump-cta"
                           className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-xs font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.01] transition"
                           style={{ background: WINE }}>
                          ☑ {data.bump_check_text || "Yes, add this too"}
                          {data.bump_price > 0 && (
                            <span className="ml-1">· ${Number(data.bump_price).toFixed(2)}</span>
                          )}
                        </a>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-base italic text-foreground/65">
                  {firstName} is curating something special for you. Check back soon.
                </p>
              )}
            </div>
          </div>
        )}

        {/* FREE GIFT — green band */}
        {hasGift && (
          <div className="rounded-2xl p-6 md:p-8 shadow-md" style={{ background: G, color: "#FFFFFF" }}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "#FFFFFF" }}>
                <Gift className="w-7 h-7" style={{ color: G }}/>
              </div>
              <div className="flex-1 min-w-[260px]">
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: GOLD }}>
                  ★ FREE GIFT FROM {firstName.toUpperCase()}
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight">
                  {data.free_gift_title || "A free gift for you"}
                </h3>
                {data.free_gift_description && (
                  <p className="text-base mt-2 leading-relaxed" style={{ color: "rgba(255,253,247,0.92)" }}>
                    {data.free_gift_description}
                  </p>
                )}
              </div>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-2 mt-2 md:mt-0 md:ml-auto">
                <a href={addRef("/free-book", code)}
                   data-testid="mirror-member-gift-cta"
                   className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] shadow-lg hover:scale-[1.03] transition"
                   style={{ background: "#FFFFFF", color: G }}>
                  <Mail className="w-4 h-4"/> {data.free_gift_cta || "Send It To Me"}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}



/* ════════════════════════════════════════════════════════════════════════
   CAPTURE-PAGE HERO — renders one of 5 designed layouts at the top of
   /m/{handle} based on m.layout. Pulls all styles from member settings.
   ════════════════════════════════════════════════════════════════════════ */

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  return m ? m[1] : null;
}
function vimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return m ? m[1] : null;
}

function VideoFrame({ url }) {
  if (!url) return null;
  const yt = youtubeId(url);
  const vm = vimeoId(url);
  if (yt) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl" style={{ border: "3px solid rgba(255,255,255,0.18)" }}>
        <iframe src={`https://www.youtube.com/embed/${yt}`} title="Video"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen/>
      </div>
    );
  }
  if (vm) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl">
        <iframe src={`https://player.vimeo.com/video/${vm}`} title="Video"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture" allowFullScreen/>
      </div>
    );
  }
  if (/\.mp4($|\?)/i.test(url) || url.startsWith("/uploads/")) {
    return (
      <video src={url} controls className="w-full aspect-video rounded-xl shadow-2xl bg-black"/>
    );
  }
  return null;
}

const HEADLINE_SIZE_CLASSES = {
  lg:   "text-2xl md:text-3xl",
  xl:   "text-3xl md:text-4xl",
  "2xl": "text-4xl md:text-5xl",
  "3xl": "text-5xl md:text-6xl",
  huge: "text-6xl md:text-7xl",
};
const SUB_SIZE_CLASSES = {
  sm: "text-sm md:text-base",
  md: "text-base md:text-lg",
  lg: "text-lg md:text-xl",
  xl: "text-xl md:text-2xl",
};
const FONT_CLASSES = {
  display: "font-serif-display",
  serif:   "font-serif",
  sans:    "",
};

function CaptureForm({ m, handle, trackEvent }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return toast.error("Please add your name and email.");
    setBusy(true);
    try {
      await api.post(`/mirror/${handle}/lead`, {
        name: f.name.trim(),
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim(),
      });
      trackEvent && trackEvent("cta");
      setDone(true);
      toast.success("\u2605 You're in! Check your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center shadow-2xl" data-testid="capture-form-success"
           style={{ border: "3px solid #2E9F3A" }}>
        <CheckCircle2 className="w-12 h-12 mx-auto mb-2" style={{ color: "#2E9F3A" }}/>
        <h3 className="font-serif-display text-2xl uppercase" style={{ color: WINE }}>You're In!</h3>
        <p className="text-sm mt-2" style={{ color: NAVY }}>Check your email for the download.</p>
      </div>
    );
  }

  const accent = m.accent_color || O;
  return (
    <form onSubmit={submit} data-testid="capture-form"
          className="rounded-2xl bg-white p-5 md:p-6 shadow-2xl space-y-3"
          style={{ border: `3px solid ${accent}` }}>
      <div className="px-3 py-1.5 rounded-md text-center"
           style={{ background: m.bg_color || NAVY, color: m.headline_color || GOLD }}>
        <div className="font-serif-display text-xl md:text-2xl uppercase leading-tight">
          Reserve Your Spot Today
        </div>
        <div className="text-[11px] mt-1 opacity-80">It's free — you can opt out anytime.</div>
      </div>
      <input value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))}
             data-testid="capture-form-name"
             placeholder="First Name"
             className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
             style={{ background: "#FFFFFF", border: `2px solid ${accent}55`, color: WINE }}/>
      <input value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))}
             data-testid="capture-form-email"
             type="email"
             placeholder="Email"
             className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
             style={{ background: "#FFFFFF", border: `2px solid ${accent}55`, color: WINE }}/>
      {m.form_collect_phone && (
        <input value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))}
               data-testid="capture-form-phone"
               placeholder="Phone (optional)"
               className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
               style={{ background: "#FFFFFF", border: `2px solid ${accent}55`, color: WINE }}/>
      )}
      <button type="submit" disabled={busy}
              data-testid="capture-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
              style={{ background: accent }}>
        {busy ? "Sending\u2026" : (m.form_button_label || "Get Instant Access")} <ArrowRight className="w-4 h-4"/>
      </button>
    </form>
  );
}

function Bullets({ items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="space-y-2 mt-4" data-testid="capture-bullets">
      {items.filter(Boolean).map((b, i) => (
        <li key={i} className="flex items-start gap-2 text-base md:text-lg" style={{ color: color || "#FFFFFF" }}>
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: "#2E9F3A" }}/>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function CapturePageHero({ m, view, handle, trackEvent }) {
  const layout = m.layout || "hero_side_form";
  const bg = m.bg_color || NAVY;
  const accent = m.accent_color || O;
  const headlineColor = m.headline_color || GOLD;
  const subColor = m.subhead_color || "#FFFFFF";
  const hClass = `${FONT_CLASSES[m.headline_font] || FONT_CLASSES.display} ${HEADLINE_SIZE_CLASSES[m.headline_size] || HEADLINE_SIZE_CLASSES["2xl"]} uppercase leading-[1.05]`;
  const subClass = `${SUB_SIZE_CLASSES[m.subhead_size] || SUB_SIZE_CLASSES.lg}`;

  const headline = view?.headline || m.headline || `Welcome \u2014 I'm ${(m.full_name||"").split(' ')[0]}.`;
  const subhead  = view?.tagline  || m.tagline  || "";
  const photo    = view?.photo_url || m.photo_url || "";
  const productImage = m.product_image_url || "";
  const bullets = m.bullets || [];

  if (layout === "hero_side_form") {
    return (
      <section className="border-b-2" style={{ background: bg, borderColor: accent }} data-testid="capture-hero-hero-side-form">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 grid lg:grid-cols-[1fr_420px] gap-8 lg:gap-10 items-center">
          <div className="flex items-start gap-5">
            {photo ? (
              <img src={photo} alt={m.full_name} loading="lazy"
                   className="rounded-full shadow-2xl flex-shrink-0"
                   style={{ width: 130, height: 130, border: `4px solid ${accent}`, objectFit: "cover", objectPosition: "center top" }}/>
            ) : (
              <div className="rounded-full shadow-2xl flex items-center justify-center flex-shrink-0"
                   style={{ width: 130, height: 130, border: `4px solid ${accent}`, background: "rgba(255,255,255,0.08)" }}>
                <div className="text-center px-2">
                  <div className="font-bold uppercase tracking-[0.14em]" style={{ color: headlineColor, fontSize: 10, lineHeight: 1.2 }}>
                    Place Your<br/>Image Here
                  </div>
                </div>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className={hClass} style={{ color: headlineColor, textWrap: "balance" }} data-testid="capture-headline">
                {headline}
              </h1>
              {subhead && <p className={`mt-3 ${subClass}`} style={{ color: subColor }} data-testid="capture-subhead">{subhead}</p>}
              <Bullets items={bullets} color={subColor}/>
            </div>
          </div>
          {m.form_enabled ? (
            <CaptureForm m={m} handle={handle} trackEvent={trackEvent}/>
          ) : productImage ? (
            <div className="rounded-xl overflow-hidden shadow-2xl" style={{ border: `3px solid ${accent}` }}>
              <img src={productImage} alt="Product" className="w-full h-auto"/>
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  if (layout === "video_top") {
    return (
      <section className="border-b-2" style={{ background: bg, borderColor: accent }} data-testid="capture-hero-video-top">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <h1 className={`text-center ${hClass}`} style={{ color: headlineColor, textWrap: "balance" }}>{headline}</h1>
          {subhead && <p className={`text-center mt-3 max-w-2xl mx-auto ${subClass}`} style={{ color: subColor }}>{subhead}</p>}
          {m.video_url && (
            <div className="mt-6 max-w-3xl mx-auto">
              <VideoFrame url={m.video_url}/>
            </div>
          )}
          <Bullets items={bullets} color={subColor}/>
          {m.form_enabled && (
            <div className="mt-6 max-w-xl mx-auto">
              <CaptureForm m={m} handle={handle} trackEvent={trackEvent}/>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (layout === "banner_bullets") {
    return (
      <section className="border-b-2" style={{ background: bg, borderColor: accent }} data-testid="capture-hero-banner-bullets">
        {productImage && (
          <div className="w-full max-h-[420px] overflow-hidden">
            <img src={productImage} alt="Banner" className="w-full h-auto object-cover"/>
          </div>
        )}
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14 text-center">
          <h1 className={hClass} style={{ color: headlineColor, textWrap: "balance" }}>{headline}</h1>
          {subhead && <p className={`mt-3 ${subClass}`} style={{ color: subColor }}>{subhead}</p>}
          {bullets.length > 0 && (
            <div className="mt-5 max-w-md mx-auto text-left">
              <Bullets items={bullets} color={subColor}/>
            </div>
          )}
          {m.form_enabled ? (
            <div className="mt-6 max-w-md mx-auto">
              <CaptureForm m={m} handle={handle} trackEvent={trackEvent}/>
            </div>
          ) : (m.upsell_cta_url && (
            <a href={m.upsell_cta_url} target="_blank" rel="noopener noreferrer"
               onClick={() => trackEvent && trackEvent("cta")}
               className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.03] transition"
               style={{ background: accent }}>
              {m.upsell_cta_label || "Get It Now"} <ArrowRight className="w-4 h-4"/>
            </a>
          ))}
        </div>
      </section>
    );
  }

  if (layout === "book_optin") {
    const cover = productImage || photo;
    return (
      <section className="border-b-2" style={{ background: bg, borderColor: accent }} data-testid="capture-hero-book-optin">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 grid md:grid-cols-2 gap-8 items-center">
          {cover && (
            <div className="flex justify-center">
              <img src={cover} alt="Book cover" loading="lazy"
                   className="rounded-xl shadow-2xl max-w-[320px] w-full"
                   style={{ border: `3px solid ${accent}` }}/>
            </div>
          )}
          <div>
            <h1 className={hClass} style={{ color: headlineColor, textWrap: "balance" }}>{headline}</h1>
            {subhead && <p className={`mt-3 ${subClass}`} style={{ color: subColor }}>{subhead}</p>}
            <Bullets items={bullets} color={subColor}/>
            <div className="mt-5">
              <CaptureForm m={{ ...m, form_enabled: true, form_button_label: m.form_button_label || "Send Me The Free Download" }}
                           handle={handle} trackEvent={trackEvent}/>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // split_image_form (default fallthrough)
  return (
    <section className="border-b-2" style={{ background: bg, borderColor: accent }} data-testid="capture-hero-split-image-form">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 grid md:grid-cols-2 gap-8 items-center">
        <div>
          {productImage ? (
            <img src={productImage} alt="" className="rounded-xl shadow-2xl w-full"
                 style={{ border: `3px solid ${accent}` }}/>
          ) : photo ? (
            <img src={photo} alt={m.full_name} className="rounded-xl shadow-2xl w-full"
                 style={{ border: `3px solid ${accent}` }}/>
          ) : null}
        </div>
        <div>
          <h1 className={hClass} style={{ color: headlineColor, textWrap: "balance" }}>{headline}</h1>
          {subhead && <p className={`mt-3 ${subClass}`} style={{ color: subColor }}>{subhead}</p>}
          <Bullets items={bullets} color={subColor}/>
          {m.form_enabled ? (
            <div className="mt-5">
              <CaptureForm m={m} handle={handle} trackEvent={trackEvent}/>
            </div>
          ) : m.upsell_cta_url ? (
            <a href={m.upsell_cta_url} target="_blank" rel="noopener noreferrer"
               onClick={() => trackEvent && trackEvent("cta")}
               className="mt-5 inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.03] transition"
               style={{ background: accent }}>
              {m.upsell_cta_label || "Get It Now"} <ArrowRight className="w-4 h-4"/>
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}
